import { useEffect, useRef, useState } from "react";
import templeImage from "@/assets/home-main.jpg";
import templeImageWebp from "@/assets/home-main.webp";
import { fetchAPI } from "@/lib/api";
import { throttle } from "@/lib/utils";

export const Introduction = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const imageRef = useRef<HTMLDivElement>(null);
  // Replaced state with direct DOM manipulation
  
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll(".reveal").forEach((el, i) => {
              setTimeout(() => {
                el.classList.add("visible");
              }, i * 150);
            });
          }
        });
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const handleScroll = throttle(() => {
      if (!sectionRef.current || !imageRef.current) return;
      const rect = sectionRef.current.getBoundingClientRect();
      const scrollProgress = (window.innerHeight - rect.top) / (window.innerHeight + rect.height);
      const offset = (scrollProgress - 0.5) * 40;
      
      // Direct DOM update
      imageRef.current.style.transform = `translateY(${offset}px)`;
    }, 50); // Throttle to 50ms

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <section
      id="introduction"
      ref={sectionRef}
      className="py-24 lg:py-32 bg-background relative overflow-hidden"
    >
      {/* Subtle background pattern */}
      <div className="absolute inset-0 bg-sacred-glow opacity-50" />

      <div className="container mx-auto px-6 lg:px-8 relative">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Text Content */}
          <div className="order-2 lg:order-1">
            <div className="reveal">
              <span className="text-primary text-sm tracking-[0.2em] uppercase font-medium">
                Reactivating the Spiritual Circuits
              </span>
            </div>

            <h2 className="reveal font-serif text-3xl md:text-4xl lg:text-5xl text-foreground mt-4 mb-6 leading-tight">
              Not Just Travel.
              <br />
              <span className="text-primary">A Sacred Journey.</span>
            </h2>

            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <p className="reveal">
                Bael Leaves is dedicated to preserving and sharing India's timeless heritage 
                through authentic journeys. We highlight diverse traditions, historical landmarks, 
                and sacred sites that have drawn seekers for millennia.
              </p>
              <p className="reveal">
                Our approach is inspired by "India's Heritage"—exploring the deep relationship 
                between temples, time, and the cultural complexity of India's sacred geography. 
                Each journey we design reflects this understanding.
              </p>
              <p className="reveal">
                Whether you seek the transformative power of ancient temples, the healing wisdom 
                of Ayurveda, or the profound silence of Himalayan retreats, we guide you through 
                India's living spiritual heritage with authenticity and care.
              </p>
            </div>

            {/* Quote */}
            <blockquote className="reveal mt-8 pl-6 border-l-2 border-primary">
              <p className="font-serif text-lg text-foreground italic">
                "The real voyage of discovery consists not in seeking new landscapes, 
                but in having new eyes."
              </p>
              <cite className="text-sm text-muted-foreground mt-2 block">— Marcel Proust</cite>
            </blockquote>
          </div>

          {/* Image with Parallax */}
          <div className="order-1 lg:order-2 reveal">
            <div 
              ref={imageRef}
              className="relative"
              style={{
                // Initial transform
                transform: "translateY(0px)",
                transition: "transform 0.1s linear" // Smoother direct update
              }}
            >
              <div className="absolute -inset-4 bg-gradient-to-br from-primary/20 to-primary/5 rounded-2xl blur-2xl animate-glow-pulse" />
              <picture>
                <source srcSet={templeImageWebp} type="image/webp" />
                <img 
                  src={templeImage}
                  alt="Sacred temple interior with spiritual atmosphere"
                  loading="lazy"
                  decoding="async"
                  className="relative w-full h-[500px] object-cover rounded-2xl shadow-elevated"
                />
              </picture>
              {/* Decorative overlay */}
              <div className="absolute bottom-6 left-6 right-6 bg-background/90 backdrop-blur-sm rounded-xl p-4 shadow-soft">
                <p className="text-sm text-muted-foreground">
                  <span className="text-primary font-medium">6 Spiritual Circuits</span> • 
                  <span className="text-primary font-medium ml-1">Authentic Experiences</span> • 
                  <span className="text-primary font-medium ml-1">Local Experts</span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
